import { action, computed, makeObservable } from "mobx";
import { TKey } from "../../../../shared/translations/translationKeys";
import { ActivityEventType } from "../../../activities/types";
import type { Api } from "../../../core/apis";
import type { Stores } from "../../../core/stores";
import BaseModuleDataModel, {
  type BaseModuleModelData,
  ModuleDataType,
} from "../../core/BaseModuleDataModel";
import PollModuleModel from "../PollModuleModel";
import { PollOptionUI } from "./PollOptionUI";
import type PollVoteModel from "./PollVoteModel";
import type { PollVoteModelData } from "./PollVoteModel";

export interface PollOptionModelData extends BaseModuleModelData {
  option: string;
}

export default class PollOptionModel extends BaseModuleDataModel<PollOptionModelData> {
  constructor(stores: Stores, api: Api, data: PollOptionModelData) {
    super(stores, api, data);

    makeObservable(this);
  }

  @action setOption(option: string) {
    const oldOption = this.data.option;

    if (oldOption !== option) {
      this.data.option = option;
      this.updateTimestamp();
      this.api.modulesDataApi.set(this);

      this.module.updateSnapshotWithoutSave();
      this.module.save();

      this.module.postActivityEvent(
        ActivityEventType.DataChanged,
        TKey.ActivityPollOptionUpdated,
        {
          oldValue: oldOption,
          newValue: this.data.option,
        },
      );
    }
  }

  @action async setAsVoted() {
    const item = this.userVoteItem;

    const module = this.module;

    if (!module.isEditable) {
      return false;
    }

    if (PollModuleModel.is(module)) {
      const multipleVotesAllowed = module.data.settings.multipleVotes;

      let votedForOptionNow = true;

      if (item) {
        if (item.data.votes.includes(this.uid)) {
          item.data.votes = item.data.votes.filter((v) => v !== this.uid);
          // Stopped voting for option
          votedForOptionNow = false;
        } else {
          if (multipleVotesAllowed) {
            item.data.votes.push(this.uid);
          } else {
            item.data.votes = [this.uid];
          }
        }
        await this.api.modulesDataApi.set(item);
      } else {
        await this.module.addNewData<PollVoteModelData>(
          ModuleDataType.PollVote,
          {
            userId: this.userUid,
            votes: [this.uid],
          },
          // Activity sent below
        );
      }

      this.module.updateSnapshotWithoutSave();
      await this.api.modulesApi.update(this.data.moduleUid, {
        snapshot: this.module.data.snapshot,
      });

      if (votedForOptionNow) {
        this.module.postActivityEvent(
          ActivityEventType.DataChanged,
          TKey.ActivityPollVotedFor,
          {
            label: this.data.option,
          },
        );
      } else {
        this.module.postActivityEvent(
          ActivityEventType.DataChanged,
          TKey.ActivityPollStoppedVotingFor,
          {
            label: this.data.option,
          },
        );
      }
    }
  }

  @computed get pollVotes() {
    if (PollModuleModel.is(this.module)) {
      return this.module.pollVotes;
    }

    throw Error("Invalid parent module found");
  }

  @computed get userUid() {
    const uid = this.plan.userInPlan?.uid;
    if (!uid) {
      throw Error("User not found in plan");
    }
    return uid;
  }

  @computed get userVoteItem(): PollVoteModel | undefined {
    return this.pollVotes.find((voteUser) => {
      return voteUser.data.userId === this.userUid;
    });
  }

  @computed get hasVotedForThis(): boolean {
    return !!this.pollVotes.find((voteUser) => {
      return (
        voteUser.data.userId === this.userUid &&
        voteUser.data.votes.includes(this.uid)
      );
    });
  }

  @computed get usersVotedArray(): string[] {
    return this.pollVotes
      .filter((voteUser) => {
        return voteUser.data.votes.includes(this.uid);
      })
      .map(
        (userVoted) =>
          this.stores.usersStore.byId.get(userVoted.data.userId)?.data
            .displayName ?? "",
      );
  }

  @computed get usersVotedStr(): string {
    return this.usersVotedArray.join(", ");
  }

  @computed get isInDoneResult(): boolean {
    if (PollModuleModel.is(this.module)) {
      return !!this.module.data.selectedOptions.find((s) => s === this.uid);
    }
    return false;
  }

  toFireStore(): Omit<PollOptionModelData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...super.toFireStore(),
      ...this.data,
    };
  }

  render() {
    return <PollOptionUI key={this.uid} data={this} />;
  }

  static is(
    module: BaseModuleDataModel<BaseModuleModelData>,
  ): module is PollOptionModel {
    return module instanceof PollOptionModel;
  }
}
